import { Box, Stack, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";

const Section1 = ({
  primary,
  activePodcast,
  latestPodcast,
  iframeRef,
  links,
}) => {
  const { section_header, section_title, section_description, section_image } =
    primary;

  const latestEpisodeName = useMemo(() => {
    if (!latestPodcast) return null;

    return latestPodcast.name.split(" ").slice(1).join(" ");
  }, [latestPodcast]);

  const latestEpisodeNumber = useMemo(() => {
    if (!latestPodcast) return null;

    const epNumber = latestPodcast.name.split(" ")?.[0]?.split("-")?.[1];

    return `Episode ${epNumber}`;
  }, [latestPodcast]);

  const theme = useTheme();

  return (
    <Stack
      sx={{
        height: "760px",
        backgroundImage: `url("${section_image.url}")`,
        backgroundPosition: "bottom",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        pt: { xs: "80px", sm: "150px" },
        position: "relative",
        px: "20px",
      }}
    >
      <Stack
        sx={{ maxWidth: "1240px", width: " 100%", alignSelf: "center" }}
        gap={4}
      >
        <Stack sx={{ maxWidth: "413px", width: " 100%" }} gap={4}>
          <Stack>
            <Typography
              variant="h1"
              sx={{ color: "#ffffff", fontWeight: "900", fontSize: "48px" }}
            >
              {section_header.text}
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontWeight: "900",
                fontSize: "48px",
                color: theme.palette.primary.main,
                mb: "10px",
              }}
            >
              {section_title.text}
            </Typography>

            <Typography sx={{ color: theme.palette.secondary.light }}>
              {section_description.text}
            </Typography>
          </Stack>

          {latestPodcast && (
            <Stack gap={1}>
              <Stack
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  alignSelf: "start",
                  width: "134.5px",
                  borderRadius: "5px",
                  padding: "2.4px 22.5px 2.4px 22px",
                  height: "18.8px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "#ffffff",
                    textTransform: "uppercase",
                    textAlign: "center",
                    lineHeight: 1,
                  }}
                >
                  Latest Episode
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{ color: theme.palette.primary.main }}
              >
                {latestEpisodeNumber}
              </Typography>

              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: theme.palette.secondary.light,
                  lineHeight: 1,
                }}
              >
                {latestEpisodeName}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack gap={1} sx={{ alignSelf: "start" }}>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.secondary.light }}
          >
            Check all episodes on:
          </Typography>

          <Stack
            direction="row"
            gap={{ xs: 2, sm: 3 }}
            sx={{
              alignItems: "center",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              maxWidth: { xs: "320px", sm: "auto" },
              width: "100%",
            }}
          >
            {links.map((link, index) => (
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  textDecoration: "none",
                  flexShrink: 0,
                  flexBasis: { xs: "calc(50% - 24px)", sm: "auto" },
                  flexGrow: { xs: 1, sm: 0 },
                }}
                key={index}
                gap={1}
                component="a"
                href={link.item_cta_link.url}
                target={link.item_cta_link.target}
              >
                <Box
                  component="img"
                  src={link.item_image.url}
                  sx={{
                    width: "30px",
                    height: "30px",
                  }}
                />

                <Typography
                  sx={{
                    color: theme.palette.secondary.light,
                    fontSize: "12px",
                    textTransform: "uppercase",
                  }}
                >
                  {link.item_cta_text.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>

      {activePodcast && (
        <Stack
          sx={{
            maxWidth: "1240px",
            width: "100%",
            "@media screen and (max-width: 1260px) ": {
              px: "20px",
            },
            height: "152px",
            position: "absolute",
            bottom: "0",
            left: "50%",
            transform: "translate(-50%, 50%)",
            borderRadius: "15px",
            overflow: "hidden",
          }}
        >
          <Box
            component="iframe"
            src={`https://open.spotify.com/embed/episode/${activePodcast.id}?utm_source=generator`}
            width="100%"
            height="100%"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            sx={{ border: "none" }}
            ref={iframeRef}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default Section1;
